import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getProductById, getRelatedProducts, allProducts } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import ScrollReveal from "@/components/base/ScrollReveal";
import NutritionModal from "@/components/base/NutritionModal";
import type { Product } from "@/mocks/products";

export default function ProdutoPage() {
  const { id } = useParams<{ id: string }>();
  const { addItem, items, updateQuantity, openCart } = useCart();
  const [activeImage, setActiveImage] = useState(0);
  const [cartQty, setCartQty] = useState(1);
  const [nutritionProduct, setNutritionProduct] = useState<Product | null>(null);
  const product = id ? getProductById(id) : undefined;
  const related = id ? getRelatedProducts(id, 4) : [];

  useEffect(() => {
    setActiveImage(0);
    setCartQty(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const inCartQty = id ? (items.find((i) => i.product.id === id)?.quantity || 0) : 0;

  const handleAddToCart = () => {
    if (product) {
      addItem(product, cartQty);
      setCartQty(1);
    }
  };

  if (!product) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4"></i>
          <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-2">
            Produto não encontrado
          </h1>
          <p className="text-foreground-500 mb-6">
            O produto que você procura não está disponível no momento.
          </p>
          <Link
            to="/cardapio"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar ao Cardápio
          </Link>
        </div>
      </section>
    );
  }

  const galleryImages = product.gallery.length > 0 ? product.gallery : [product.image];

  const whatsappMsg = `Olá! Gostaria de pedir ${product.name}. Poderia me passar mais informações?`;
  const whatsappLink = `https://wa.me/558534942701?text=${encodeURIComponent(whatsappMsg)}`;

  return (
    <>
      {/* Breadcrumb */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 pt-24 pb-4 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Início</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link to="/cardapio" className="hover:text-primary-600 transition-colors cursor-pointer">Cardápio</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <section className="relative w-full py-8 md:py-14 bg-background-50 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Gallery */}
            <ScrollReveal direction="up">
              <div className="flex flex-col gap-3">
                {/* Main Image */}
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-background-200 border border-background-200/50">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeImage}
                      src={galleryImages[activeImage]}
                      alt={`${product.name} — imagem ${activeImage + 1}`}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="w-full h-full object-cover"
                    />
                  </AnimatePresence>

                  {product.badge && (
                    <span className="absolute top-4 left-4 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-xs font-bold uppercase tracking-wider shadow-lg">
                      {product.badge}
                    </span>
                  )}
                </div>

                {/* Thumbnails */}
                {galleryImages.length > 1 && (
                  <div className="flex gap-2">
                    {galleryImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                          activeImage === idx
                            ? "border-primary-500 shadow-md"
                            : "border-background-200 hover:border-primary-300"
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Miniatura ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollReveal>

            {/* Product Info */}
            <ScrollReveal direction="up" delay={0.15}>
              <div className="flex flex-col">
                {/* Category & Tags */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider">
                    {product.category === "sanduiches-naturais"
                      ? "Sanduíche Natural"
                      : "Mini Pastel"}
                  </span>
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[10px] font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {product.name}
                </h1>

                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  {product.fullDescription}
                </p>

                {/* Quick Specs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                  {[
                    { icon: "ri-scales-3-line", label: "Peso", value: product.weight },
                    { icon: "ri-fridge-line", label: "Conservação", value: product.storageTemp },
                    { icon: "ri-calendar-check-line", label: "Validade", value: product.shelfLife },
                    { icon: "ri-fire-line", label: "Calorias", value: `${product.nutrition.calories} kcal` },
                  ].map((spec) => (
                    <div
                      key={spec.label}
                      className="p-3 rounded-xl bg-background-100 border border-background-200/50 flex flex-col items-center text-center"
                    >
                      <i className={`${spec.icon} text-primary-500 text-lg mb-1`}></i>
                      <span className="text-[10px] text-foreground-400 uppercase tracking-wider">
                        {spec.label}
                      </span>
                      <span className="text-xs font-semibold text-foreground-800">
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Price & CTA */}
                <div className="flex flex-col gap-4 mb-8 p-5 rounded-2xl bg-background-100 border border-background-200/50">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-foreground-400 uppercase tracking-wider">
                        {product.category === "mini-pasteis"
                          ? "Preço da caixa (100 un)"
                          : "Preço unitário"}
                      </span>
                      <span className="text-3xl font-heading font-bold text-primary-600">
                        R$ {product.price.toFixed(2)}
                      </span>
                    </div>

                    {inCartQty > 0 ? (
                      <div className="sm:ml-auto flex items-center gap-2">
                        <span className="text-sm text-foreground-500">
                          {inCartQty}x no carrinho
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => updateQuantity(product.id, inCartQty - 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                            {inCartQty}
                          </span>
                          <button
                            onClick={() => updateQuantity(product.id, inCartQty + 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                        <button
                          onClick={openCart}
                          className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Ver Carrinho
                        </button>
                      </div>
                    ) : (
                      <div className="sm:ml-auto flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setCartQty((q) => Math.max(1, q - 1))}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                            {cartQty}
                          </span>
                          <button
                            onClick={() => setCartQty((q) => q + 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                        <button
                          onClick={handleAddToCart}
                          className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-400 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-shopping-bag-3-line"></i>
                          Adicionar ao carrinho
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/50">
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 group inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-500 text-background-50 text-sm font-semibold hover:bg-green-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                      Pedir direto pelo WhatsApp
                    </a>
                  </div>
                </div>

                {/* Ingredients */}
                <div className="mb-8">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground-700 mb-3">
                    Ingredientes
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.ingredients.map((ing) => (
                      <span
                        key={ing}
                        className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/50 text-sm text-foreground-700"
                      >
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Conservation */}
                <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/40">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 shrink-0">
                      <i className="ri-fridge-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">Conservação</p>
                      <p className="text-sm text-foreground-600">{product.conservation}</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Nutrition Table */}
          <ScrollReveal direction="up" delay={0.2} className="mt-12 md:mt-16">
            <div className="rounded-2xl bg-background-100 border border-background-200/50 overflow-hidden">
              <div className="px-6 py-4 border-b border-background-200/50">
                <h2 className="text-xl font-heading font-semibold text-foreground-950">
                  Informação Nutricional
                </h2>
                <p className="text-sm text-foreground-500">
                  Valores nutricionais {product.nutrition.unit}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6">
                {[
                  { label: "Calorias", value: product.nutrition.calories, unit: "kcal", icon: "ri-fire-line" },
                  { label: "Proteínas", value: product.nutrition.proteins, unit: "g", icon: "ri-restaurant-line" },
                  { label: "Carboidratos", value: product.nutrition.carbs, unit: "g", icon: "ri-bread-line" },
                  { label: "Gorduras", value: product.nutrition.fats, unit: "g", icon: "ri-drop-line" },
                  { label: "Sódio", value: product.nutrition.sodium, unit: "mg", icon: "ri-restaurant-2-line" },
                  { label: "Fibras", value: product.nutrition.fiber, unit: "g", icon: "ri-seedling-line" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col items-center p-5 border-b border-r border-background-200/30 last:border-r-0"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-2">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="text-2xl font-heading font-bold text-foreground-950">
                      {item.value}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {item.unit}
                    </span>
                    <span className="text-xs text-foreground-500 mt-1">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              {product.detailedNutrition && (
                <div className="px-6 py-4 border-t border-background-200/50 flex justify-center">
                  <button
                    onClick={() => setNutritionProduct(product)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-list-3-line"></i>
                    </span>
                    Ver tabela nutricional completa
                  </button>
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="relative w-full py-16 md:py-24 bg-background-100 overflow-hidden">
          <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <ScrollReveal className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                Produtos relacionados
              </h2>
              <p className="mt-2 text-foreground-500">
                Você também pode gostar dessas opções
              </p>
            </ScrollReveal>

            <ScrollReveal direction="up" staggerChildren staggerDelay={0.1}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/produto/${p.id}`}
                    className="group flex flex-col rounded-2xl bg-background-50 border border-background-200/40 overflow-hidden hover:border-primary-300/50 hover:shadow-lg transition-all duration-500 hover:-translate-y-1"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {p.badge && (
                        <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold uppercase">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-4">
                      <span className="text-[10px] text-foreground-400 uppercase tracking-wider mb-1">
                        {p.category === "sanduiches-naturais" ? "Sanduíche" : "Mini Pastel"}
                      </span>
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 flex-1">
                        {p.shortDescription}
                      </p>
                      <span className="text-base font-heading font-semibold text-primary-600">
                        R$ {p.price.toFixed(2)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* All Products CTA */}
      <section className="relative w-full py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 text-center">
          <Link
            to="/cardapio"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restaurant-line"></i>
            Ver todos os produtos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {nutritionProduct && nutritionProduct.detailedNutrition && (
        <NutritionModal
          isOpen={true}
          onClose={() => setNutritionProduct(null)}
          data={nutritionProduct.detailedNutrition}
          productName={nutritionProduct.name}
        />
      )}
    </>
  );
}